// Generic outline machinery: chains boundary segments (world px) into closed
// loops, then smooths them. Topologies emit segments with the faction interior
// consistently on the LEFT of travel, so an outer boundary and a hole punched
// in it come out wound in opposite directions.
//
// That winding is what tells the two kinds apart, but it is *not* enough to
// make a renderer subtract one from the other on its own — the renderer
// triangulates each sub-path of a fill independently, so a hole handed to it
// as just another polygon is painted, not cut (R96). `loopsWithHoles()` below
// does the pairing explicitly for callers that need it.

export type Point = [number, number];
export type Loop = Point[];
export type Segment = [number, number, number, number]; // sx, sy, ex, ey

// Corners are matched by rounded-px key: distinct corners in either topology
// are >= ~20px apart, float noise is nanometers, so 1px rounding is safe.
// The row stride is 2^16, so a world up to about 32 000 px wide keeps two
// corners apart, and the key stays an exact integer while |y| < 2^37 (R2239).
const KEY_STRIDE = 1 << 16;
const pkey = (x: number, y: number) => Math.round(y) * KEY_STRIDE + Math.round(x);

export function chainLoops(segments: Segment[]): Loop[] {
  const bySrc = new Map<number, { ex: number; ey: number }[]>();
  const starts = new Map<number, Point>();
  for (const [sx, sy, ex, ey] of segments) {
    const k = pkey(sx, sy);
    const list = bySrc.get(k);
    if (list) {
      list.push({ ex, ey });
    } else {
      bySrc.set(k, [{ ex, ey }]);
      starts.set(k, [sx, sy]);
    }
  }

  const loops: Loop[] = [];
  while (bySrc.size > 0) {
    const [startKey, ends] = bySrc.entries().next().value as [number, { ex: number; ey: number }[]];
    const start = starts.get(startKey)!;
    const loop: Loop = [start];
    let end = ends.pop()!;
    if (ends.length === 0) bySrc.delete(startKey);
    let px = start[0], py = start[1];
    let cx = end.ex, cy = end.ey;

    while (pkey(cx, cy) !== startKey) {
      loop.push([cx, cy]);
      const k = pkey(cx, cy);
      const candidates = bySrc.get(k);
      if (!candidates || candidates.length === 0) break; // degenerate input; bail safely
      let pick = 0;
      if (candidates.length > 1) {
        // Pinch corner (square grids only): sharpest right turn keeps loops separate.
        const inX = cx - px, inY = cy - py;
        let best = -Infinity;
        for (let i = 0; i < candidates.length; i++) {
          const cross = inX * (candidates[i].ey - cy) - inY * (candidates[i].ex - cx);
          if (cross > best) { best = cross; pick = i; }
        }
      }
      const next = candidates[pick];
      candidates[pick] = candidates[candidates.length - 1];
      candidates.pop();
      if (candidates.length === 0) bySrc.delete(k);
      px = cx; py = cy;
      cx = next.ex; cy = next.ey;
    }
    if (loop.length >= 3) loops.push(collapseCollinear(loop));
  }
  return loops;
}

/**
 * Shoelace area. Only the sign matters here: because segments are emitted
 * with the interior on the left, an outer boundary and a hole punched in
 * it come out with opposite signs.
 */
export function loopArea(loop: readonly Point[]): number {
  let a = 0;
  for (let i = 0, n = loop.length; i < n; i++) {
    const p = loop[i], q = loop[(i + 1) % n];
    a += p[0] * q[1] - q[0] * p[1];
  }
  return a / 2;
}

/**
 * Which winding direction means "outer" in this particular loop set.
 *
 * Read from the data rather than assumed: the largest loop in any set is
 * necessarily an outer one, since a hole always fits inside its container.
 * Returns 0 when the set is degenerate (no area at all) and the question
 * has no answer.
 */
function outerSign(loops: Loop[]): number {
  let outer = 0;
  for (const l of loops) {
    const a = loopArea(l);
    if (Math.abs(a) > Math.abs(outer)) outer = a;
  }
  return Math.sign(outer);
}

/**
 * The outer boundaries of a traced shape, with its holes dropped.
 *
 * This exists for the *stroke*: a glow drawn round an enclosed pocket lights
 * the pocket up from every side at once, which made an unclaimed tile inside
 * sovereign water the brightest thing on the map (R90).
 */
export function outerLoops(loops: Loop[]): Loop[] {
  if (loops.length < 2) return loops;
  const sign = outerSign(loops);
  if (sign === 0) return loops;
  return loops.filter((l) => Math.sign(loopArea(l)) === sign);
}

/** An outer boundary together with the holes punched through it. */
export interface LoopWithHoles {
  outer: Loop;
  holes: Loop[];
}

/**
 * The counterpart `outerLoops()` leaves out: the same set regrouped so every
 * outer boundary carries the holes that belong to *it*.
 *
 * This is what a fill needs, and it needs it spelled out. The winding is
 * already correct — holes run against their container — but the renderer walks
 * the sub-paths of one fill and triangulates each in isolation, so a hole
 * offered as just another polygon is filled in the faction's colour instead
 * of being cut out of it. The only mechanism that actually subtracts is
 * `GraphicsContext.cut()`, and cut() attaches its hole to the *last shape of
 * the last fill instruction* — so the caller has to emit one fill per outer
 * loop and hand it exactly that loop's holes. Hence the pairing: it is the
 * fill's job to know which pocket sits in which territory (R96).
 *
 * Containment is tested rather than inferred, because a faction routinely
 * holds several disjoint territories and "the hole and the boundary came out
 * of the same trace" says nothing about which blob the hole is in. Where a
 * hole sits inside more than one candidate — an island in a pocket in a
 * territory, which the tracer emits as two outer loops nested around one
 * hole — the *smallest* container wins, since that is the shape the hole is
 * actually punched through.
 *
 * Holes with no container are dropped. That cannot happen for a well-formed
 * trace, and painting one would put a solid blob of faction colour on open
 * water, which is the exact bug this function exists to remove.
 */
export function loopsWithHoles(loops: Loop[]): LoopWithHoles[] {
  const sign = outerSign(loops);
  if (sign === 0) return loops.map((outer) => ({ outer, holes: [] }));

  const outers: LoopWithHoles[] = [];
  const holes: Loop[] = [];
  for (const l of loops) {
    if (Math.sign(loopArea(l)) === sign) outers.push({ outer: l, holes: [] });
    else holes.push(l);
  }
  if (holes.length === 0 || outers.length === 0) return outers;

  // Bounding boxes first: territory loops run to hundreds of vertices after
  // Chaikin, and most (outer, hole) pairs are nowhere near each other. This
  // runs on sovereignty change, not per frame (§10), but it is cheap enough
  // to keep it that way.
  const boxes = outers.map((o) => bounds(o.outer));
  for (const hole of holes) {
    // Any vertex of the hole does: a hole loop and its container are traced
    // from different cell edges, so no vertex is ever shared between them and
    // the test never lands on a boundary.
    const [hx, hy] = hole[0];
    let best = -1;
    let bestArea = Infinity;
    for (let i = 0; i < outers.length; i++) {
      const b = boxes[i];
      if (hx < b[0] || hx > b[2] || hy < b[1] || hy > b[3]) continue;
      if (!pointInLoop(hx, hy, outers[i].outer)) continue;
      const a = Math.abs(loopArea(outers[i].outer));
      if (a < bestArea) { bestArea = a; best = i; }
    }
    if (best >= 0) outers[best].holes.push(hole);
  }
  return outers;
}

/** Axis-aligned bounds as [minX, minY, maxX, maxY]. */
function bounds(loop: Loop): [number, number, number, number] {
  let minX = Infinity, minY = Infinity, maxX = -Infinity, maxY = -Infinity;
  for (const [x, y] of loop) {
    if (x < minX) minX = x;
    if (x > maxX) maxX = x;
    if (y < minY) minY = y;
    if (y > maxY) maxY = y;
  }
  return [minX, minY, maxX, maxY];
}

// Ray casting, winding-agnostic on purpose: the caller has already used the
// winding to decide what is an outer loop and what is a hole, and this only
// has to answer "is this point enclosed".
function pointInLoop(x: number, y: number, loop: Loop): boolean {
  let inside = false;
  for (let i = 0, j = loop.length - 1; i < loop.length; j = i++) {
    const [xi, yi] = loop[i], [xj, yj] = loop[j];
    if ((yi > y) !== (yj > y) && x < ((xj - xi) * (y - yi)) / (yj - yi) + xi) inside = !inside;
  }
  return inside;
}

function collapseCollinear(loop: Loop): Loop {
  const out: Loop = [];
  const n = loop.length;
  for (let i = 0; i < n; i++) {
    const a = loop[(i - 1 + n) % n], b = loop[i], c = loop[(i + 1) % n];
    const cross = (b[0] - a[0]) * (c[1] - b[1]) - (b[1] - a[1]) * (c[0] - b[0]);
    if (Math.abs(cross) > 0.5) out.push(b);
  }
  return out.length >= 3 ? out : loop;
}

// Laplacian relaxation on a closed loop: pulls each vertex toward the midpoint
// of its neighbors. Flattens the zigzag that runs of same-row/column cells
// produce (amplitude roughly halves per iteration at strength 0.5), so long
// straight borders become actually straight, while large-scale curves survive.
//
// A border that runs *diagonally* across the hex grid is the slow case, and
// the one the eye picks up as a regular wave: its staircase repeats over two
// to three cells rather than two vertices, so each pass shaves only ~8% off
// it (measured: RMS deviation from the ideal line, 15deg cut, 6.5px at 2
// passes, 4.0px at 6, 3.0px at 10). Getting that ripple down to something
// the eye reads as a curve therefore costs iterations, not strength — which
// is what `restoreArea` below exists to make affordable.
export function relaxLoop(loop: Loop, iterations: number, strength = 0.5): Loop {
  let pts = loop;
  for (let it = 0; it < iterations; it++) {
    const n = pts.length;
    const next: Loop = new Array(n);
    for (let i = 0; i < n; i++) {
      const p = pts[i], a = pts[(i - 1 + n) % n], b = pts[(i + 1) % n];
      next[i] = [
        p[0] * (1 - strength) + ((a[0] + b[0]) / 2) * strength,
        p[1] * (1 - strength) + ((a[1] + b[1]) / 2) * strength,
      ];
    }
    pts = next;
  }
  return pts;
}

/**
 * Scale a relaxed loop about its centroid until it encloses `target` area
 * again.
 *
 * Laplacian relaxation shrinks: every pass moves each vertex toward the
 * chord between its neighbours, which for a closed loop is always inward.
 * On a territory the size of a claim disc that is a rounding error (0.3%
 * at 2 passes, 0.7% at 6), but it scales with the inverse of the loop, and
 * a *small* claim is eaten alive — a single owned cell keeps 32% of its
 * area at 2 passes and 3% at 6. That is what capped the iteration count,
 * not the look of a long border.
 *
 * Restoring the area afterwards decouples the two: the ripple falls with
 * every pass, and the shape still covers the cells it owns at any count.
 * The two factions either side of a shared border shrink away from it
 * equally, so putting the area back also puts them back in contact instead
 * of leaving a hairline of open water between them.
 *
 * Scaling — rather than an outward normal offset — because it cannot
 * self-intersect and needs no winding argument: a hole comes out of
 * `loopArea` with the opposite sign but the same magnitude, so the same
 * factor grows the pocket as grows the territory.
 */
export function restoreArea(loop: Loop, target: number): Loop {
  const a = Math.abs(loopArea(loop));
  if (a <= 0 || target <= 0) return loop;
  const k = Math.sqrt(target / a);
  let cx = 0, cy = 0;
  for (const [x, y] of loop) { cx += x; cy += y; }
  cx /= loop.length; cy /= loop.length;
  return loop.map(([x, y]) => [cx + (x - cx) * k, cy + (y - cy) * k] as Point);
}

// Chaikin corner cutting on a closed loop; 2 rounds hides the cell structure.
export function smoothLoop(loop: Loop, iterations = 2): Loop {
  let pts = loop;
  for (let it = 0; it < iterations; it++) {
    const next: Loop = [];
    const n = pts.length;
    for (let i = 0; i < n; i++) {
      const a = pts[i], b = pts[(i + 1) % n];
      next.push([a[0] * 0.75 + b[0] * 0.25, a[1] * 0.75 + b[1] * 0.25]);
      next.push([a[0] * 0.25 + b[0] * 0.75, a[1] * 0.25 + b[1] * 0.75]);
    }
    pts = next;
  }
  return pts;
}
